import { useEffect, useMemo, useRef, useState } from "react";
import { Crown, Shield, Users, Search, UserPlus, Link2, Copy, Check, X } from "lucide-react";
import { io } from "socket.io-client";
import { useAuth } from "../../context/AuthContext";

function pickFirstNonNull(...vals) {
  for (const v of vals) {
    if (v !== undefined && v !== null && String(v).trim() !== "") return v;
  }
  return null;
}

function normalizeRole(role) {
  const r = String(role || "").trim().toLowerCase();

  if (["owner", "creator"].includes(r)) return "Owner";
  if (["moderator", "mod", "admin"].includes(r)) return "Moderator";
  return "Member";
}

function normalizeMembershipStatus(status) {
  return String(status || "").trim().toLowerCase();
}

function normalizePresence(status) {
  const s = String(status || "").trim().toLowerCase();

  if (["online", "active", "available"].includes(s)) return "online";
  if (["idle", "away", "busy", "dnd", "do-not-disturb"].includes(s)) return "online";
  return "offline";
}

function resolveMemberAvatar(url) {
  if (!url) return null;
  let s = String(url).trim();
  if (!s) return null;
  if (s.startsWith("undefined/")) s = s.replace(/^undefined\/+/, "");
  if (/^https?:\/\//i.test(s)) return s;
  if (/^(data:|blob:)/i.test(s)) return s;

  const r2Base = (process.env.REACT_APP_R2_PUBLIC_BASE || "").trim();
  if (r2Base && /^users\//i.test(s)) {
    return `${r2Base.replace(/\/+$/, "")}/${s.replace(/^\/+/, "")}`;
  }

  const rooted = s.startsWith("/") ? s : `/${s}`;
  const apiBase = (process.env.REACT_APP_API_BASE || "").trim();
  if (apiBase) return `${apiBase.replace(/\/+$/, "")}${rooted}`;
  if (typeof window !== "undefined") {
    const { protocol, hostname, port } = window.location;
    if (
      (hostname === "localhost" || hostname === "127.0.0.1") &&
      (port === "3000" || port === "5173")
    ) {
      return `${protocol}//${hostname}:5000${rooted}`;
    }
  }
  return rooted;
}

function getRawMembers(club) {
  return (
    club?.members_list ??
    club?.membersList ??
    club?.member_list ??
    club?.memberList ??
    club?.members_data ??
    club?.membersData ??
    club?.memberships ??
    club?.member_memberships ??
    club?.club_members ??
    club?.clubMembers ??
    club?.members_rows ??
    club?.memberRows ??
    []
  );
}

function normalizeMembers(club) {
  const rawMembers = Array.isArray(getRawMembers(club)) ? getRawMembers(club) : [];

  const normalized = rawMembers
    .map((m, idx) => {
      const nestedUser = m?.user ?? m?.member ?? m?.profile ?? null;

      const membershipStatus = normalizeMembershipStatus(
        pickFirstNonNull(
          m?.membership_status,
          m?.membershipStatus,
          m?.club_status,
          m?.clubStatus,
          m?.status
        )
      );

      if (membershipStatus && !["active", "member", "joined"].includes(membershipStatus)) {
        return null;
      }

      const presence = normalizePresence(
        pickFirstNonNull(
          nestedUser?.presence,
          nestedUser?.online_status,
          nestedUser?.onlineStatus,
          nestedUser?.status,
          m?.presence,
          m?.online_status,
          m?.onlineStatus
        )
      );

      const id = pickFirstNonNull(
        m?.id,
        m?.user_id,
        m?.userId,
        m?.member_id,
        m?.memberId,
        nestedUser?.id,
        nestedUser?.user_id,
        nestedUser?.userId,
        `${idx}`
      );

      const name =
        pickFirstNonNull(
          m?.username,
          m?.name,
          m?.display_name,
          m?.displayName,
          m?.handle,
          m?.full_name,
          m?.fullName,
          nestedUser?.username,
          nestedUser?.name,
          nestedUser?.display_name,
          nestedUser?.displayName,
          nestedUser?.handle,
          nestedUser?.full_name,
          nestedUser?.fullName
        ) || "Unknown";

      const role = normalizeRole(
        pickFirstNonNull(
          m?.role,
          m?.member_role,
          m?.memberRole,
          m?.membership_role,
          m?.membershipRole,
          nestedUser?.role
        )
      );

      const profilePicture = pickFirstNonNull(
        m?.profile_picture,
        m?.profilePicture,
        m?.avatar,
        m?.avatar_url,
        m?.avatarUrl,
        m?.profile_image,
        m?.profileImage,
        nestedUser?.profile_picture,
        nestedUser?.profilePicture,
        nestedUser?.avatar,
        nestedUser?.avatar_url,
        nestedUser?.avatarUrl,
        nestedUser?.profile_image,
        nestedUser?.profileImage
      );

      return {
        id,
        name,
        role,
        presence,
        profile_picture: profilePicture,
        search_text: String(
          pickFirstNonNull(
            m?.username,
            m?.name,
            m?.display_name,
            m?.displayName,
            m?.full_name,
            m?.fullName,
            nestedUser?.username,
            nestedUser?.name,
            nestedUser?.display_name,
            nestedUser?.displayName,
            nestedUser?.full_name,
            nestedUser?.fullName,
            " "
          )
        ).toLowerCase(),
      };
    })
    .filter(Boolean);

  const hasOwnerInList = normalized.some((m) => m.role === "Owner");

  if (!hasOwnerInList) {
    const ownerName = pickFirstNonNull(
      club?.owner_username,
      club?.owner_name,
      club?.created_by_username,
      club?.createdByUsername,
      club?.creator_username,
      club?.creatorName
    );

    const ownerId = pickFirstNonNull(
      club?.owner_user_id,
      club?.ownerUserId,
      club?.owner_id,
      club?.ownerId,
      club?.created_by,
      club?.createdBy,
      club?.created_by_user_id,
      club?.createdByUserId
    );

    const ownerProfilePicture = pickFirstNonNull(
      club?.owner_profile_picture,
      club?.ownerProfilePicture,
      club?.created_by_profile_picture,
      club?.createdByProfilePicture
    );

    if (ownerName || ownerId) {
      normalized.unshift({
        id: ownerId || "owner",
        name: ownerName || "Owner",
        role: "Owner",
        presence: "online",
        profile_picture: ownerProfilePicture || null,
      });
    }
  }

  const seen = new Set();

  return normalized.filter((person, idx) => {
    const key = String(
      pickFirstNonNull(person.id, `${person.name}-${person.role}-${idx}`)
    );

    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function ClubMembersTab({ club }) {
  const [search, setSearch] = useState("");
  const [failedAvatarIds, setFailedAvatarIds] = useState(() => new Set());
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteCopied, setInviteCopied] = useState(false);

  const { user } = useAuth();

  const pickAuthUserId = (u) => {
    const keys = [u?.id, u?.user_id, u?.userId, u?.uid, u?.sub, u?.profile?.id, u?.profile?.user_id, u?.account?.id];
    for (const v of keys) {
      if (v == null || v === "") continue;
      const n = Number(v);
      if (Number.isFinite(n)) return n;
    }
    return null;
  };

  const viewerId = pickAuthUserId(user);

  const allMembersBase = useMemo(() => normalizeMembers(club), [club]);
  const filteredMembersBase = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allMembersBase;
    return allMembersBase.filter((person) => {
      const byName = String(person.name || "").toLowerCase().includes(q);
      const byIndex = String(person.search_text || "").includes(q);
      return byName || byIndex;
    });
  }, [allMembersBase, search]);

  const memberIdsToWatch = useMemo(() => {
    const ids = allMembersBase.map((m) => Number(m.id)).filter((n) => Number.isFinite(n));
    return Array.from(new Set(ids));
  }, [allMembersBase]);

  const memberIdsKey = useMemo(
    () => memberIdsToWatch.slice().sort((a, b) => a - b).join(","),
    [memberIdsToWatch]
  );

  // AnimeHouse-wide global presence (from friend-chat socket tracking).
  const [onlineUserIds, setOnlineUserIds] = useState(() => new Set());
  const socketRef = useRef(null);

  useEffect(() => {
    if (!viewerId) return;

    const apiBase = process.env.REACT_APP_API_BASE || "";
    const socketOrigin =
      apiBase || (process.env.NODE_ENV === "development" ? "http://localhost:5000" : window.location.origin);

    const socket = io(`${socketOrigin}/friend-chat`, { withCredentials: true });
    socketRef.current = socket;

    socket.on("connect", () => {
      socket.emit("user-online", { userId: viewerId });
    });

    socket.on("presence:bulk-status", (payload) => {
      const raw = Array.isArray(payload?.onlineUserIds) ? payload.onlineUserIds : [];
      const next = new Set(raw.map((x) => Number(x)).filter((n) => Number.isFinite(n)));
      setOnlineUserIds(next);
    });

    socket.on("presence:user-status", ({ userId, isOnline }) => {
      const id = Number(userId);
      if (!Number.isFinite(id)) return;
      setOnlineUserIds((prev) => {
        const next = new Set(prev);
        if (isOnline) next.add(id);
        else next.delete(id);
        return next;
      });
    });

    return () => {
      try {
        socket.disconnect();
      } catch {
        // ignore
      }
      socketRef.current = null;
      setOnlineUserIds(new Set());
    };
  }, [viewerId]);

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !viewerId) return;
    if (!memberIdsToWatch.length) {
      setOnlineUserIds(new Set());
      return;
    }
    socket.emit("presence:bulk", { userIds: memberIdsToWatch });
  }, [viewerId, memberIdsKey, memberIdsToWatch]);

  const filteredMembers = filteredMembersBase;

  const onlineMembers = useMemo(() => {
    return filteredMembers.filter((person) => {
      const pid = Number(person.id);
      if (!Number.isFinite(pid)) return person.presence === "online";
      return onlineUserIds.has(pid);
    });
  }, [filteredMembers, onlineUserIds]);

  const offlineMembers = useMemo(() => {
    return filteredMembers.filter((person) => {
      const pid = Number(person.id);
      if (!Number.isFinite(pid)) return person.presence !== "online";
      return !onlineUserIds.has(pid);
    });
  }, [filteredMembers, onlineUserIds]);

  const inviteUrl = useMemo(() => {
    const slug = String(club?.slug || "").trim();
    if (!slug) return "";
    if (typeof window !== "undefined" && window.location?.origin) {
      return `${window.location.origin}/clubs/${encodeURIComponent(slug)}`;
    }
    return `/clubs/${encodeURIComponent(slug)}`;
  }, [club?.slug]);

  const onInviteClick = () => {
    setInviteCopied(false);
    setInviteOpen(true);
  };

  const closeInvitePanel = () => {
    setInviteOpen(false);
    setInviteCopied(false);
  };

  const onCopyInvite = async () => {
    if (!inviteUrl) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(inviteUrl);
      } else {
        const input = document.createElement("input");
        input.value = inviteUrl;
        document.body.appendChild(input);
        input.select();
        document.execCommand("copy");
        document.body.removeChild(input);
      }
      setInviteCopied(true);
      window.setTimeout(() => setInviteCopied(false), 1800);
    } catch {
      // Ignore copy failure and keep modal open for manual copy.
    }
  };

  const getRolePillStyles = (role) => {
    if (role === "Owner") {
      return "border-[#7ee7ff]/20 bg-[#0d1628] text-[#8cecff]";
    }

    if (role === "Moderator") {
      return "border-white/10 bg-[#121827] text-white/70";
    }

    return "border-white/10 bg-[#101522] text-white/50";
  };

  const getRoleIcon = (role) => {
    if (role === "Owner") return <Crown className="h-3 w-3 text-[#7ee7ff]" />;
    if (role === "Moderator") return <Shield className="h-3 w-3 text-[#7ee7ff]" />;
    return <Users className="h-3 w-3 text-white/50" />;
  };

  const renderPerson = (person) => {
    const pid = Number(person.id);
    const isOnline = !Number.isFinite(pid) ? person.presence === "online" : onlineUserIds.has(pid);
    const avatarSrc = resolveMemberAvatar(person.profile_picture);
    const avatarKey = String(person.id);
    const hasBrokenAvatar = failedAvatarIds.has(avatarKey);
    const canShowAvatar = Boolean(avatarSrc) && !hasBrokenAvatar;

    return (
      <div
        key={person.id}
        className="rounded-xl border border-white/10 bg-[#0d1525] px-2.5 py-2 transition-all duration-200 hover:-translate-y-[1px] hover:border-white/20 hover:bg-[#121e34]"
      >
        <div className="flex items-center gap-2">
          <div className="relative h-9 w-9 shrink-0">
            <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full border border-white/12 bg-[#11192c] text-[13px] font-semibold leading-none text-white">
              {canShowAvatar ? (
                <img
                  src={avatarSrc}
                  alt={`${person.name || "Member"} profile`}
                  className="h-full w-full object-cover"
                  onError={() => {
                    setFailedAvatarIds((prev) => {
                      const next = new Set(prev);
                      next.add(avatarKey);
                      return next;
                    });
                  }}
                />
              ) : (
                String(person.name || "U").charAt(0).toUpperCase()
              )}
            </div>

            <span
              className={`absolute -bottom-px -right-px h-2.5 w-2.5 rounded-full border-2 border-[#0d1525] shadow-[0_0_0_1px_rgba(255,255,255,0.05)] ${
                isOnline ? "bg-emerald-400" : "bg-slate-400/80"
              }`}
            />
          </div>

          <div className="min-w-0 flex-1">
            <div className="truncate text-[13px] font-medium leading-snug text-white">
              {person.name}
            </div>

            <div className="mt-0.5">
              <span
                className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] uppercase tracking-[0.1em] leading-none ${getRolePillStyles(
                  person.role
                )}`}
              >
                {getRoleIcon(person.role)}
                {person.role}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderSection = (title, people) => (
    <>
      <div className="mt-3 px-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-white/38">
        {title} — {people.length}
      </div>

      <div className="mt-1.5 space-y-1.5">
        {people.length > 0 ? (
          people.map(renderPerson)
        ) : (
          <div className="rounded-xl border border-white/10 bg-[#0d1525] px-2.5 py-2.5 text-xs text-white/45">
            No members found.
          </div>
        )}
      </div>
    </>
  );

  return (
    <div className="relative overflow-hidden rounded-[28px] border border-white/10 bg-[#0a1120] shadow-[0_20px_60px_rgba(0,0,0,0.3)]">
      <div className="px-3 pt-3 pb-2">
        <div className="mb-2 flex flex-col gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/30" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search ${club?.name || "club"} members...`}
              className="w-full rounded-xl border border-white/10 bg-[#0a0f1d] py-2 pl-9 pr-3 text-[13px] leading-snug text-white outline-none transition focus:border-[#7ee7ff]/45 focus:ring-1 focus:ring-[#7ee7ff]/20 placeholder:text-white/28"
            />
          </div>

          <button
            type="button"
            onClick={onInviteClick}
            className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-white/10 bg-[#10182b] px-3 py-2 text-[13px] font-medium text-white/72 transition hover:border-white/20 hover:bg-[#162038] hover:text-white"
          >
            <UserPlus className="h-3.5 w-3.5 shrink-0" />
            Invite
          </button>
        </div>

        {filteredMembers.length === 0 ? (
          <div className="rounded-xl border border-white/10 bg-[#0d1525] px-2.5 py-2.5 text-xs text-white/45">
            No members found.
          </div>
        ) : (
          <>
            {renderSection("Online", onlineMembers)}
            {renderSection("Offline", offlineMembers)}
          </>
        )}
      </div>

      {inviteOpen ? (
        <div
          className="absolute inset-0 z-20 flex items-center justify-center bg-black/65 p-4 backdrop-blur-[2px]"
          onClick={closeInvitePanel}
        >
          <div
            className="w-full max-w-sm rounded-2xl border border-white/12 bg-[#0c1426] p-4 shadow-[0_18px_60px_rgba(0,0,0,0.45)]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-3 flex items-center justify-between">
              <div className="inline-flex items-center gap-2 text-sm font-semibold text-white">
                <Link2 className="h-4 w-4 text-[#8cecff]" />
                Invite to club
              </div>
              <button
                type="button"
                onClick={closeInvitePanel}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-white/12 bg-white/[0.03] text-white/70 transition hover:bg-white/[0.08] hover:text-white"
                aria-label="Close invite panel"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="mb-3 text-xs text-white/55">
              Share this link so people can open the club and join based on the club join policy.
            </p>
            <div className="rounded-xl border border-white/10 bg-black/25 px-3 py-2 text-xs text-white/80 break-all">
              {inviteUrl || "Club link unavailable (missing slug)."}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                disabled={!inviteUrl}
                onClick={onCopyInvite}
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-white/12 bg-white/[0.04] px-3 py-2 text-sm text-white/85 transition hover:bg-white/[0.08] disabled:opacity-50"
              >
                {inviteCopied ? <Check className="h-4 w-4 text-emerald-300" /> : <Copy className="h-4 w-4" />}
                {inviteCopied ? "Copied" : "Copy link"}
              </button>
              <button
                type="button"
                onClick={closeInvitePanel}
                className="inline-flex items-center justify-center rounded-xl border border-white/12 bg-transparent px-3 py-2 text-sm text-white/70 transition hover:bg-white/[0.05] hover:text-white"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default ClubMembersTab;